import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, DollarSign, RotateCcw, XCircle,
  AlertTriangle, CheckCircle2, TrendingUp, Users, Repeat, TrendingDown, ArrowUpCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const currency = (cents) =>
  `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SURFACE_LABEL = {
  booking_deposit: "Booking deposit",
  package_checkout: "Package",
  invoice: "Invoice",
  subscription: "Retainer",
};

const STATUS_STYLES = {
  paid: "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/40",
  pending: "bg-[#f59e0b]/15 text-[#f59e0b] border-[#f59e0b]/40",
  failed: "bg-[#ef4444]/15 text-[#ef4444] border-[#ef4444]/40",
  refunded: "bg-[#a78bfa]/15 text-[#a78bfa] border-[#a78bfa]/40",
  partially_refunded: "bg-[#a78bfa]/15 text-[#a78bfa] border-[#a78bfa]/40",
};

export default function AdminPayments() {
  const [rev, setRev] = useState(null);
  const [txs, setTxs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(null); // {pi, current}
  const [refundAmount, setRefundAmount] = useState("");
  const [cancelling, setCancelling] = useState(null); // {sub}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, s] = await Promise.all([
        axios.get(`${API}/admin/payments/revenue-stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/payments/transactions?limit=200`, { headers: authHeaders() }),
        axios.get(`${API}/admin/payments/subscriptions`, { headers: authHeaders() }),
      ]);
      setRev(r.data); setTxs(t.data.transactions || []); setSubs(s.data.subscriptions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payments");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refund = async () => {
    if (!refunding) return;
    setBusy(true);
    try {
      const body = {};
      const parsed = parseFloat(refundAmount);
      if (!isNaN(parsed) && parsed > 0) body.amount_cents = Math.round(parsed * 100);
      await axios.post(`${API}/admin/payments/${refunding.pi}/refund`, body, { headers: authHeaders() });
      toast.success("Refund issued.");
      setRefunding(null); setRefundAmount("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  const cancelSub = async (atPeriodEnd) => {
    if (!cancelling) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/subscriptions/${cancelling.sub.subscription_id}/cancel`,
        { at_period_end: atPeriodEnd },
        { headers: authHeaders() },
      );
      toast.success(atPeriodEnd ? "Cancels at period end." : "Cancelled immediately.");
      setCancelling(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const changePlan = async (subscription_id, package_key) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/subscriptions/${subscription_id}/change-plan`,
        { package_key },
        { headers: authHeaders() },
      );
      toast.success(`Plan changed to ${package_key}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Change plan failed");
    } finally { setBusy(false); }
  };

  const revCards = useMemo(() => {
    if (!rev) return [];
    return [
      { label: "Today", data: rev.today, color: "#00f0ff" },
      { label: "This week", data: rev.week, color: "#a78bfa" },
      { label: "This month", data: rev.month, color: "#22c55e" },
    ];
  }, [rev]);

  const retainerCards = useMemo(() => {
    if (!rev?.retainers) return [];
    const r = rev.retainers;
    return [
      {
        key: "mrr",
        label: "MRR",
        hint: `${r.active_count} active retainer${r.active_count === 1 ? "" : "s"} · $${(r.arr_cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })} ARR`,
        value: currency(r.mrr_cents),
        color: "#22c55e",
        icon: Repeat,
      },
      {
        key: "churn",
        label: "Churn · 30 days",
        hint: r.churned_30d === 0 ? "Nobody left — clean month." : "Retainers cancelled in the last 30 days.",
        value: `${r.churned_30d}`,
        color: r.churned_30d === 0 ? "#22c55e" : "#ef4444",
        icon: TrendingDown,
      },
      {
        key: "expansion",
        label: "Expansion · 30d",
        hint: "Upgrade proration revenue over the last 30 days.",
        value: currency(r.expansion_30d_cents),
        color: "#00f0ff",
        icon: ArrowUpCircle,
      },
    ];
  }, [rev]);

  return (
    <div className="min-h-screen bg-[#05050a] text-white overflow-hidden relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full blur-[120px] bg-[#22c55e]/10" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <Link to="/admin" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-3" data-testid="payments-back">
              <ArrowLeft size={14} /> Admin Hub
            </Link>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
              Payments<span className="text-[#22c55e]">.</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              Stripe revenue · one-off transactions · active retainers · refund from the row
            </p>
          </div>
          <button onClick={load} disabled={loading} data-testid="payments-refresh" className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center disabled:opacity-50" aria-label="Refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>

        {/* Revenue snapshot */}
        {rev && (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7 mb-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] flex items-center justify-center">
                <TrendingUp size={16} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">Revenue</div>
                <div className="font-display font-bold text-lg tracking-tight">Paid transactions</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {revCards.map((c) => (
                <div key={c.label} className="rounded-2xl bg-white/[0.02] border border-white/10 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">{c.label}</div>
                  <div className="font-display font-black text-2xl md:text-3xl mt-1 break-all" style={{ color: c.color }}>
                    {currency(c.data?.total_cents || 0)}
                  </div>
                  <div className="text-[11px] text-white/40 mt-1">
                    {c.data?.count || 0} payment{(c.data?.count || 0) === 1 ? "" : "s"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Retainer analytics — MRR, churn, expansion */}
        {rev?.retainers && (
          <div
            className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7 mb-6"
            data-testid="retainer-analytics"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#a78bfa]/15 border border-[#a78bfa]/40 text-[#a78bfa] flex items-center justify-center">
                <Repeat size={16} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">Subscription health</div>
                <div className="font-display font-bold text-lg tracking-tight">Retainer analytics</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {retainerCards.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.key}
                    data-testid={`retainer-tile-${c.key}`}
                    className="rounded-2xl bg-white/[0.02] border border-white/10 px-4 py-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">{c.label}</div>
                      <Icon size={14} style={{ color: c.color }} />
                    </div>
                    <div
                      className="font-display font-black text-2xl md:text-3xl mt-1 break-all"
                      style={{ color: c.color }}
                    >
                      {c.value}
                    </div>
                    <div className="text-[11px] text-white/50 mt-1.5 leading-snug">
                      {c.hint}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Transactions */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7 mb-6" data-testid="admin-payments-transactions">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-[#00f0ff] flex items-center justify-center">
              <DollarSign size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">Ledger</div>
              <div className="font-display font-bold text-lg tracking-tight">All payments · {txs.length}</div>
            </div>
          </div>
          {txs.length === 0 && <div className="text-white/40 italic py-8 text-center">No payments yet.</div>}
          <div className="space-y-2">
            {txs.map((tx) => (
              <div key={tx.payment_intent_id} data-testid={`tx-row-${tx.payment_intent_id}`} className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/10">
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate max-w-[220px] sm:max-w-none">{tx.email}</span>
                    <span className={`text-[9px] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[tx.payment_status] || "bg-white/5 text-white/50 border-white/20"}`}>
                      {tx.payment_status}
                    </span>
                    <span className="text-[10px] text-white/40">
                      {SURFACE_LABEL[tx.surface] || tx.surface}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5 font-mono-tight break-all">
                    {tx.payment_intent_id} · {tx.paid_at ? new Date(tx.paid_at).toLocaleString() : new Date(tx.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="font-mono text-sm text-white">
                  {currency(tx.amount_cents)}
                </div>
                {tx.payment_status === "paid" && (
                  <button
                    onClick={() => { setRefunding({ pi: tx.payment_intent_id, current: tx.amount_cents }); setRefundAmount(""); }}
                    data-testid={`tx-refund-${tx.payment_intent_id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ef4444]/10 border border-[#ef4444]/40 hover:bg-[#ef4444]/20 text-[#ef4444] text-xs font-semibold"
                  >
                    <RotateCcw size={11} /> Refund
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Subscriptions */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7" data-testid="admin-payments-subscriptions">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#a78bfa]/15 border border-[#a78bfa]/40 text-[#a78bfa] flex items-center justify-center">
              <Users size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">Retainers</div>
              <div className="font-display font-bold text-lg tracking-tight">Active subscriptions · {subs.length}</div>
            </div>
          </div>
          {subs.length === 0 && <div className="text-white/40 italic py-8 text-center">No active retainers yet.</div>}
          <div className="space-y-2">
            {subs.map((s) => (
              <div key={s.subscription_id} data-testid={`sub-row-${s.subscription_id}`} className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/10">
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate max-w-[220px] sm:max-w-none">{s.email || s.customer_id}</span>
                    <span className="text-[9px] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full border bg-white/[0.03] text-white/70 border-white/20">
                      {s.status}
                    </span>
                    <span className="text-[10px] text-white/40">
                      {s.package_key || "—"}
                    </span>
                    {s.cancel_at_period_end && (
                      <span className="text-[10px] text-[#f59e0b] font-semibold">· cancels at period end</span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5 font-mono-tight break-all">
                    {s.subscription_id} · updated {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ""}
                  </div>
                </div>
                <select
                  onChange={(e) => e.target.value && changePlan(s.subscription_id, e.target.value)}
                  value=""
                  data-testid={`sub-change-${s.subscription_id}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.1]"
                >
                  <option value="">Change plan…</option>
                  <option value="content_partner">→ Content Partner ($1,540/mo)</option>
                  <option value="content_engine">→ Content Engine ($1,500/mo)</option>
                  <option value="social_command">→ Social Command ($3,000/mo)</option>
                </select>
                {s.status !== "canceled" && s.status !== "cancelled" && (
                  <button
                    onClick={() => setCancelling({ sub: s })}
                    data-testid={`sub-cancel-${s.subscription_id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ef4444]/10 border border-[#ef4444]/40 hover:bg-[#ef4444]/20 text-[#ef4444] text-xs font-semibold"
                  >
                    <XCircle size={11} /> Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Refund modal */}
      {refunding && (
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRefunding(null)}>
          <div className="max-w-md w-full rounded-3xl bg-[#0a0510] border border-[#ef4444]/40 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#ef4444]/15 text-[#ef4444] flex items-center justify-center flex-shrink-0"><AlertTriangle size={18} /></div>
              <div>
                <h3 className="font-display font-black text-lg tracking-tight">Refund payment</h3>
                <p className="text-[#a395c3] text-sm mt-1">
                  Charge amount: <span className="text-white">{currency(refunding.current)}</span>.
                  Leave blank for full refund, or enter a partial amount in dollars.
                </p>
              </div>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              max={refunding.current / 100}
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              placeholder={`Full refund: ${(refunding.current / 100).toFixed(2)}`}
              data-testid="refund-amount"
              className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#ef4444]/60"
              autoFocus
            />
            <div className="mt-4 flex items-center gap-2 justify-end">
              <button onClick={() => setRefunding(null)} className="px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-xs">Cancel</button>
              <button onClick={refund} disabled={busy} data-testid="refund-confirm" className="px-4 py-2 rounded-full bg-[#ef4444] text-white text-xs font-semibold hover:bg-[#ef4444]/80 disabled:opacity-50">
                {busy && <Loader2 size={12} className="animate-spin inline mr-1" />}
                Issue refund
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel subscription modal */}
      {cancelling && (
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCancelling(null)}>
          <div className="max-w-md w-full rounded-3xl bg-[#0a0510] border border-[#ef4444]/40 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#ef4444]/15 text-[#ef4444] flex items-center justify-center flex-shrink-0"><XCircle size={18} /></div>
              <div>
                <h3 className="font-display font-black text-lg tracking-tight">Cancel this retainer?</h3>
                <p className="text-[#a395c3] text-sm mt-1">
                  <span className="text-white">{cancelling.sub.email || cancelling.sub.customer_id}</span> — {cancelling.sub.package_key}. Pick how gracefully to end things:
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => cancelSub(true)}
                disabled={busy}
                data-testid="sub-cancel-end-of-period"
                className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-white/[0.05] border border-white/15 hover:bg-white/[0.1] text-sm text-left"
              >
                <CheckCircle2 size={14} className="text-[#22c55e]" />
                <div>
                  <div className="font-semibold">At period end (recommended)</div>
                  <div className="text-white/50 text-[11px]">Client keeps what they paid for. No refund needed.</div>
                </div>
              </button>
              <button
                onClick={() => cancelSub(false)}
                disabled={busy}
                data-testid="sub-cancel-immediate"
                className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-[#ef4444]/10 border border-[#ef4444]/40 hover:bg-[#ef4444]/20 text-[#ef4444] text-sm text-left"
              >
                <XCircle size={14} />
                <div>
                  <div className="font-semibold">Immediately</div>
                  <div className="text-[#ef4444]/70 text-[11px]">Access ends now — consider refunding manually.</div>
                </div>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setCancelling(null)} className="px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-xs">Back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
